/* eslint-disable react/no-multi-comp, id-length */
import { useMemo } from "react";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeReact, { Components } from "rehype-react";
import production from "react/jsx-runtime";

import { CodeBlock, CodeBlockHeader } from "~/components/shared/StepsEditorSidebar/CodeBlock";

export function Code({ children }: React.HTMLProps<HTMLElement>) {
  return (
    <code className="not-prose rounded-md bg-purple-100 px-1 py-0.5 font-mono text-xs">
      {children}
    </code>
  );
}

function Anchor(props: React.HTMLProps<HTMLAnchorElement>) {
  return (
    <a
      {...props}
      className="text-inherit underline underline-offset-[1.5px]"
      target="_blank"
      rel="noreferrer noopener"
    />
  );
}

export function Markdown({
  children,
  components,
}: {
  children: string | undefined;
  components?: Partial<Components>;
}) {
  const markdown = useMemo(() => {
    return unified()
      .use(remarkParse)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeRaw)
      .use(rehypeSanitize, {
        tagNames: [...(defaultSchema.tagNames ?? []), "figure", "figcaption"],
      })
      .use(rehypeReact, {
        ...production,
        components: {
          a: Anchor,
          code: Code,
          figure: (props) => <figure className="not-prose" {...props} />,
          figcaption: CodeBlockHeader,
          pre: CodeBlock,
          ...components,
        },
      })
      .processSync(children).result;
  }, [children, components]);

  return (
    <div className="prose-links:underline-offset-[1.5px] prose prose-sm prose-zinc prose-headings:my-0 prose-headings:text-base prose-headings:font-semibold prose-p:my-0 prose-blockquote:my-0 prose-ol:my-0 prose-ul:my-0 space-y-2 leading-snug">
      {markdown}
    </div>
  );
}
